/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {
    Box,
    Flex,
    Button,
    Stack,
    Text,
    Divider,
    useMultiStyleConfig,
    HStack,
    VStack,
    Portal,
} from '@chakra-ui/react'
import {PromoCode, usePromoCode} from './partials/promo-code'
import ZipCodeWrapper, {useZipCode} from './partials/zip-code-preview'
import {useLocation} from 'react-router-dom'
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import {usePromotions, useProducts} from '@salesforce/commerce-sdk-react'
import {remCalc} from '../../theme/_utils'
import ItemPriceCustom from '../item-variant/item-price-custom'
import OrderSummaryDivider from './partials/divider'
import {CheckIcon, DeleteIcon, InfoIcon} from '../icons'
import KlarnaWidget from '../klarna-widget'
import VatLabel from '../vat-label'
import useUtils from '../../custom-hooks/use-utils-helper'
import InfoPopover from '../popovers/info-popover'
import WeeeFee from '../weee-fee'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import OrderTotal from './partials/orderTotal'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import Checkbox from '../checkbox'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import Link from '@salesforce/retail-react-app/app/components/link'
import SubscriptionItem from '../subscription-item'
import PaymentMethodLabel from '../payment-method-label'

const CartItems = ({basket, productsByItemId}) => {
    const totalItems = basket?.productItems?.reduce((acc, item) => acc + item.quantity, 0) || 0
    const location = useLocation()
    const {isCart} = useUtils().currentPage(location.pathname)
    const styles = useMultiStyleConfig('OrderSummary', {basketLength: basket?.productItems?.length})
    const allSubscriptionsDetails = basket?.c_allSubscriptionsDetails
        ? JSON.parse(basket.c_allSubscriptionsDetails)
        : {}

    return (
        <Box sx={styles.cartItems.container}>
            {isCart && (
                <Text {...styles.cartItems.itemsText}>
                    <FormattedMessage
                        defaultMessage="Items ({totalItems})"
                        id="order_summary.cart_items.link.items_in_cart"
                        values={{totalItems}}
                    />
                </Text>
            )}
            {Object.entries(allSubscriptionsDetails).map(([bundleId, details]) => {
                return (
                    <Box key={bundleId} {...styles.cartItems.itemsContainer}>
                        <SubscriptionItem details={details} variant={'subscriptionsOrderSummary'} />
                        <Divider mt="medium.base" />
                    </Box>
                )
            })}
            {basket.productItems?.map((product, idx) => {
                if (product?.c_isSubscription === 'true') return
                const variant = {
                    ...product,
                    ...(productsByItemId && productsByItemId[product.itemId]),
                    price: product?.price,
                    quantity: product?.quantity,
                }
                return (
                    <Box key={idx} {...styles.cartItems.itemsContainer}>
                        <ItemVariantProvider index={idx} variant={variant}>
                            <Flex {...styles.cartItems.innerItemContainer}>
                                <CartItemVariantImage
                                    imageProps={{width: remCalc(109)}}
                                    boxWidth={remCalc(109)}
                                    minWidth={remCalc(109)}
                                />
                                <Stack {...styles.cartItems.stack}>
                                    <CartItemVariantName {...styles.cartItems.itemVariant} />
                                    <Flex {...styles.cartItems.stackContent}>
                                        <ItemPriceCustom
                                            currency={basket?.currency}
                                            styles={{}}
                                            direction="row"
                                            showOmnibusLabel={false}
                                            styleVariant="checkout"
                                        />
                                        <CartItemVariantAttributes
                                            includeQuantity
                                            includeVariables={false}
                                            {...styles.cartItems.itemVariantAttributes}
                                        />
                                    </Flex>
                                </Stack>
                            </Flex>
                        </ItemVariantProvider>
                        <Divider mt="medium.base" />
                    </Box>
                )
            })}
        </Box>
    )
}

CartItems.propTypes = {basket: PropTypes.object, productsByItemId: PropTypes.object}

const OrderSummary = ({
    basket,
    // showPromoCodeForm = false,
    showCartItems = false,
    isEstimate = false,
    showPromoCodeForm = true,
    variant,
    productsByItemId,
    handleUnavailableProducts,
    showSubscriptionsCheck = false,
    step,
}) => {
    const {removePromoCode, ...promoCodeProps} = usePromoCode()
    const {...zipCodeProps} = useZipCode()
    const style = useMultiStyleConfig('OrderSummary', {variant})
    const location = useLocation()
    const isVATFlag = useBooleanCustomPreference('showVATFlag')
    const avalaraStorefrontPreference = useBooleanCustomPreference('avalaraStorefrontPreference')
    const isAvalaraEnabled =
        avalaraStorefrontPreference !== null ? avalaraStorefrontPreference : false
    const navigate = useNavigation()
    const {formatMessage} = useIntl()
    const [salesTermsAccepted, setSalesTermsAccepted] = useState(false)
    const [salesTermsError, setSalesTermsError] = useState(false)
    const termsCheckboxRef = useRef(null)

    const bundles = basket?.c_additionalData?.allBundlesDetails || {}

    const {locale} = useMultiSite()

    const {refetch: refetchProducts} = useProducts(
        {
            parameters: {
                ids: basket?.productItems?.map((i) => i.productId).join(','),
                allImages: false,
            },
            headers: {
                'x-debug': 'order-summary-index',
            },
        },
        {
            enabled: false,
        },
    )

    const productPriceAdjustments =
        basket?.productItems?.reduce((acc, product) => {
            if (product.priceAdjustments) {
                acc.push(
                    ...product.priceAdjustments.filter(
                        (adj) => !adj.promotionId?.startsWith('bundle'),
                    ),
                )
            }
            return acc
        }, []) ?? []

    const weeeFeeItems = productPriceAdjustments.filter((item) => item.promotionId === 'weeeFee')

    // Sum the prices of those items
    const weeeFeeTotal = weeeFeeItems.reduce((sum, item) => sum + item.price, 0)

    const allPriceAdjustments = [
        ...(productPriceAdjustments ?? []),
        ...(basket?.orderPriceAdjustments ?? []),
    ]
    const allPromotionIds = allPriceAdjustments.map((adj) => adj?.promotionId)

    const {data: promos} = usePromotions(
        {parameters: {ids: allPromotionIds.join(',')}},
        {enabled: allPromotionIds.length > 0},
    )

    const {isCart, isCheckout} = useUtils().currentPage(location.pathname)

    if (!basket?.basketId && !basket?.orderNo) {
        return null
    }
    // const shippingItem = basket?.shippingItems?.[0]
    // const hasShippingPromos = shippingItem?.priceAdjustments?.length > 0

    const calcSubtotal = (basket) => {
        let subtotal = 0
        // to calculate the subtotal, the prices of all items are added together except for free gifts, which are identified as type bonus
        if (basket?.productItems) {
            for (let item of basket.productItems) {
                const hasBonusAdjustment = item.priceAdjustments?.some(
                    (adjustment) => adjustment.appliedDiscount?.type === 'bonus',
                )

                if (!hasBonusAdjustment) {
                    subtotal += item.price
                }
            }
        }

        return subtotal || 0
    }

    const handleGoToCheckout = async () => {
        if (showSubscriptionsCheck && !salesTermsAccepted) {
            setSalesTermsError(true)
            termsCheckboxRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
            return
        }

        const ids = basket?.productItems?.map((i) => i.productId)
        const result = await refetchProducts()

        const resProductIds = []
        const unOrderableIds = []
        result?.data?.data?.forEach(({id, inventory}) => {
            resProductIds.push(id)
            const item = basket.productItems.find((i) => i.productId === id)
            const isWishlist = !!item?.type
            if (
                !isWishlist &&
                (!inventory?.orderable ||
                    (inventory?.orderable && item.quantity > inventory.stockLevel))
            ) {
                unOrderableIds.push(id)
            }
        })

        const unavailable = ids.filter(
            (id) => !resProductIds.includes(id) || unOrderableIds.includes(id),
        )

        if (unavailable.length > 0) {
            handleUnavailableProducts(unavailable)
        } else {
            navigate('/checkout')
        }
    }

    return (
        <Box {...style?.orderSummaryBox}>
            {isCart && (
                <>
                    {showSubscriptionsCheck && (
                        <Box {...style?.subscriptionsContainer}>
                            <Flex ref={termsCheckboxRef}>
                                <Box>
                                    <Checkbox
                                        variant={'secondary'}
                                        onChange={(e) => {
                                            setSalesTermsAccepted(e.target.checked)
                                            setSalesTermsError(false)
                                        }}
                                        isError={salesTermsError}
                                    />
                                </Box>
                                <Flex {...style?.subscriptionsTextsContainer}>
                                    <Text {...style?.subscriptionsText}>
                                        <FormattedMessage
                                            id="order_summary.subscriptions.accept_text"
                                            defaultMessage="I accept the"
                                        />
                                    </Text>
                                    <Link to={`${locale?.saleTermsAndConditions}`}>
                                        <Text {...style?.subscriptionsLink}>
                                            <FormattedMessage
                                                id="order_summary.subscriptions.accept_link"
                                                defaultMessage="terms and conditions of sale"
                                            />
                                        </Text>
                                    </Link>
                                </Flex>
                            </Flex>
                            {salesTermsError && (
                                <Text {...style?.errorText}>
                                    <FormattedMessage
                                        id="order_summary.subscriptions.terms_error"
                                        defaultMessage="*Obbligatory field"
                                    />
                                </Text>
                            )}
                        </Box>
                    )}
                    {showPromoCodeForm ? (
                        <Box w="full">
                            <PromoCode {...promoCodeProps} />
                        </Box>
                    ) : (
                        <Stack {...style.promo.promoContainer}>
                            <Text {...style.promoCode.inputContainerHeader}>
                                <FormattedMessage
                                    id="use_promo_code_fields.label.promo_code"
                                    defaultMessage="Promo Code"
                                />
                            </Text>

                            {basket?.couponItems?.length > 0 && (
                                <Flex {...style.promoCode.textContainer}>
                                    <Flex {...style.promoCode.couponSuccessContainer}>
                                        <CheckIcon {...style.promoCode.couponSuccessIcon} />
                                        <Text {...style?.promoCode?.textCoupon}>
                                            <FormattedMessage
                                                id="order_summary.coupon"
                                                defaultMessage="Coupon"
                                            />
                                            {': '}
                                            {basket.couponItems[0]?.code}
                                        </Text>
                                    </Flex>
                                    {!basket.orderNo && (
                                        <DeleteIcon
                                            cursor="pointer"
                                            onClick={() =>
                                                removePromoCode(basket.couponItems[0]?.couponItemId)
                                            }
                                            aria-label={formatMessage({
                                                id: 'order_summary.promo_code.remove.aria_label',
                                                defaultMessage: 'Remove promo code',
                                            })}
                                            role="button"
                                        />
                                    )}
                                </Flex>
                            )}
                        </Stack>
                    )}
                </>
            )}

            <Stack {...style?.summaryContainer} data-testid="sf-order-summary">
                <Box {...style?.innerSummaryContainer}>
                    <Box {...style?.summaryHeader?.itemContainer}>
                        <Text {...style?.summaryHeader?.headerText} id="order-summary-heading">
                            <FormattedMessage
                                defaultMessage="Order Review"
                                id="order_summary.heading.order_review"
                            />
                        </Text>
                    </Box>
                    {showCartItems && isCart && (
                        <>
                            <OrderSummaryDivider />
                            <CartItems basket={basket} productsByItemId={productsByItemId} />
                            <OrderSummaryDivider />
                        </>
                    )}

                    <Box {...style?.subtotalSection?.subtotalContainer}>
                        <HStack {...style?.subtotalSection?.subtotalStackContainer}>
                            <Text {...style?.subtotalSection?.subtotalText}>
                                <FormattedMessage
                                    defaultMessage="Subtotal"
                                    id="order_summary.label.subtotal"
                                />
                            </Text>
                            <Text {...style?.subtotalSection?.subtotalText}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={calcSubtotal(basket)}
                                />
                            </Text>
                        </HStack>
                    </Box>

                    <VStack {...style?.subtotalSection?.shippingContainer}>
                        <HStack {...style?.subtotalSection?.subtotalStackContainer}>
                            <HStack>
                                <Text {...style?.subtotalSection?.subtotalText}>
                                    <FormattedMessage
                                        defaultMessage="Delivery"
                                        id="order_summary.label.shipping"
                                    />
                                </Text>
                                <Box {...style?.subtotalSection?.infoIconContainer}>
                                    <InfoPopover
                                        variant="top-right"
                                        trigger={
                                            <InfoIcon
                                                {...style?.subtotalSection?.infoIcon}
                                                aria-hidden="true"
                                                tabIndex={0}
                                                focusable="true"
                                            />
                                        }
                                        openTooltipOn="hover"
                                        title={formatMessage({
                                            id: 'order_summary.delivery.info.title',
                                            defaultMessage: 'Delivery Information',
                                        })}
                                        description={formatMessage({
                                            id: 'order_summary.delivery.info.description',
                                            defaultMessage:
                                                'Delivery details and estimated arrival time',
                                        })}
                                    />
                                </Box>
                            </HStack>
                            {basket?.shippingTotal > 0 ? (
                                <Text {...style?.subtotalSection?.subtotalText}>
                                    <FormattedNumber
                                        value={basket.shippingTotal}
                                        style="currency"
                                        currency={basket?.currency}
                                    />
                                </Text>
                            ) : (
                                <Text {...style?.subtotalSection?.freeShippingText}>
                                    <FormattedMessage
                                        defaultMessage="Free shipping"
                                        id="order_summary.label.free_shipping"
                                    />
                                </Text>
                            )}
                        </HStack>
                        <PaymentMethodLabel basket={basket} style={style} />
                    </VStack>
                    {allPriceAdjustments.map(
                        (adjustment) =>
                            adjustment.promotionId !== 'weeeFee' && (
                                <Box
                                    key={adjustment.priceAdjustmentId}
                                    {...style.subtotalSection.subtotalPriceContainer}
                                >
                                    <Text {...style?.subtotalSection?.subtotalText}>
                                        <Text {...style?.subtotalSection?.subtotalText}>
                                            {
                                                promos?.data?.find(
                                                    (promo) => promo.id === adjustment.promotionId,
                                                )?.calloutMsg
                                            }
                                        </Text>
                                    </Text>
                                    <Text {...style.subtotalSection.subtotalText}>
                                        <FormattedNumber
                                            style="currency"
                                            currency={basket?.currency}
                                            value={adjustment.price}
                                        />
                                    </Text>
                                </Box>
                            ),
                    )}
                    {bundles &&
                        Object.entries(bundles).map(([bundleId, details]) => {
                            const discount =
                                details?.totalPrice - details?.totalDiscountedPrice || 0
                            if (discount > 0) {
                                return (
                                    <Box
                                        key={bundleId}
                                        {...style.subtotalSection.subtotalPriceContainer}
                                    >
                                        <Text {...style?.subtotalSection?.subtotalText}>
                                            {details?.bundleName}
                                        </Text>
                                        <Text {...style.subtotalSection.subtotalText}>
                                            <FormattedNumber
                                                style="currency"
                                                currency={basket?.currency}
                                                value={discount * -1}
                                            />
                                        </Text>
                                    </Box>
                                )
                            }
                        })}
                    {weeeFeeTotal > 0 && <WeeeFee price={weeeFeeTotal} variant="orderSummary" />}
                    {isAvalaraEnabled && (
                        <Box>
                            <ZipCodeWrapper
                                flex-direction="column"
                                {...zipCodeProps}
                                basket={basket}
                                {...style.zipCode.zipCodeContainer}
                                step={step}
                                key={`${basket?.productSubTotal} ${
                                    !step || step === 1 || step === 2
                                        ? 'showInputStep'
                                        : 'notShowInputStep'
                                }`}
                            />
                        </Box>
                    )}
                </Box>

                <OrderSummaryDivider />

                {(isCart || isCheckout) && (
                    <Box {...style?.checkoutSection?.checkoutContainer}>
                        {isEstimate ? (
                            <Text {...style?.checkoutSection?.totalText}>
                                <FormattedMessage
                                    defaultMessage="TOTAL"
                                    id="order_summary.label.estimated_total"
                                />
                            </Text>
                        ) : (
                            <Text {...style?.checkoutSection?.totalText}>
                                <FormattedMessage
                                    defaultMessage="Order Total"
                                    id="order_summary.label.order_total"
                                />
                            </Text>
                        )}
                        <Text {...style?.checkoutSection?.totalText}>
                            <FormattedNumber
                                style="currency"
                                currency={basket?.currency}
                                value={basket?.orderTotal || basket?.productTotal}
                            />
                        </Text>
                    </Box>
                )}

                {(isCart || isCheckout) && (
                    <Box gap={remCalc(8)} pb={isCheckout && 4}>
                        {isVATFlag && <VatLabel amount={basket?.taxTotal} hideVatRate />}
                        {isCart && (
                            <KlarnaWidget
                                price={{currentPrice: basket?.orderTotal}}
                                variant={'cart'}
                            ></KlarnaWidget>
                        )}
                    </Box>
                )}

                {/* This is to add space beneath the footer to ensure it is not covered by the fixed to the bottom goToCheckout banner when in mobile or tablet view.
                    This is achieved using a Portal with position: relative and a negative z-index.*/}
                {isCart && (
                    <Portal>
                        <Box
                            {...style?.checkoutSection?.goToCheckoutButtonContainer}
                            position={'relative'}
                            zIndex={-1}
                            display={{base: 'block', lg: 'none'}}
                        >
                            <OrderTotal
                                isEstimate={isEstimate}
                                isVATFlag={isVATFlag}
                                basket={basket}
                            />
                            <Button
                                as="button"
                                onClick={handleGoToCheckout}
                                {...style?.checkoutSection?.goToCheckout}
                                variant="primary"
                                aria-label="Proceed to checkout"
                                sx={{
                                    touchAction: 'manipulation',
                                    '& > *': {
                                        pointerEvents: 'none',
                                    },
                                }}
                            >
                                <Text>
                                    <FormattedMessage
                                        id="order_summary.checkout_button"
                                        defaultMessage="Go to Checkout"
                                    />
                                </Text>
                            </Button>
                        </Box>
                    </Portal>
                )}

                {/* This is the sticky goToCheckout button banner that is fixed at the bottom of the screen*/}
                {isCart && (
                    <Box
                        {...style?.checkoutSection?.goToCheckoutButtonContainer}
                        {...style?.checkoutSection?.goToCheckoutButtonContainerSticky}
                    >
                        <OrderTotal isEstimate={isEstimate} isVATFlag={isVATFlag} basket={basket} />
                        <Button
                            as="button"
                            {...style?.checkoutSection?.goToCheckout}
                            onClick={async () => {
                                handleGoToCheckout()
                            }}
                            variant="primary"
                            aria-label="Proceed to checkout"
                            sx={{
                                touchAction: 'manipulation',
                                '& > *': {
                                    pointerEvents: 'none',
                                },
                            }}
                        >
                            <Text>
                                <FormattedMessage
                                    id="order_summary.checkout_button"
                                    defaultMessage="Go to Checkout"
                                />
                            </Text>
                        </Button>
                    </Box>
                )}

                {showCartItems && isCheckout && (
                    <>
                        <OrderSummaryDivider />
                        <CartItems basket={basket} productsByItemId={productsByItemId} />
                        {/* <OrderSummaryDivider /> */}
                    </>
                )}
                {/* 
                <Text>
                    <FormattedMessage defaultMessage="Tax" id="order_summary.label.tax" />
                </Text>
                {basket.taxTotal != null ? (
                    <Text fontSize={fontSize}>
                        <FormattedNumber
                            value={basket.taxTotal}
                            style="currency"
                            currency={basket.currency}
                        />
                    </Text>
                ) : (
                    <Text fontSize={fontSize} color="gray.700">
                        TBD
                    </Text>
                )} 
                 */}
            </Stack>
        </Box>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    variant: PropTypes.string,
    showPromoCodeForm: PropTypes.bool,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool,
    fontSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    totalItems: PropTypes.number,
    productsByItemId: PropTypes.object,
    handleUnavailableProducts: PropTypes.func,
    showSubscriptionsCheck: PropTypes.bool,
    step: PropTypes.any,
}

export default OrderSummary
